/* ============================================================
   Erasia — global.js
   Header behaviour, AJAX cart, cart drawer, toasts, carousels,
   quantity inputs, count-up stats, recently-viewed tracking.
   No dependencies.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- utilities ---------- */
  window.eraMoney = function (cents) {
    var format = window.moneyFormat || '{{ amount }}';
    var value = (cents / 100).toFixed(2);
    if (/amount_no_decimals/.test(format)) value = Math.round(cents / 100).toString();
    if (/amount_with_comma_separator/.test(format)) value = value.replace('.', ',');
    value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  };

  window.eraToast = function (message) {
    var container = document.getElementById('ToastContainer');
    if (!container) return;
    var toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML =
      '<span class="toast__check" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 11 11"><path d="M2 5.5l2.2 2.2L9 3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
      '<span class="toast__msg"></span>';
    toast.querySelector('.toast__msg').textContent = message;
    container.appendChild(toast);
    setTimeout(function () { toast.remove(); }, 2600);
  };

  /* ---------- sticky header shrink ---------- */
  var lastState = false;
  function onScroll() {
    var y = window.scrollY;
    var scrolled = lastState ? y > 12 : y > 72; /* hysteresis: no flicker at the boundary */
    if (scrolled !== lastState) {
      lastState = scrolled;
      document.body.classList.toggle('scrolled-past', scrolled);
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- focus trap utility ---------- */
  function getFocusable(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement; }
    );
  }
  function trapFocus(container) {
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var els = getFocusable(container);
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeydown);
    return function () { document.removeEventListener('keydown', onKeydown); };
  }

  /* ---------- mobile menu drawer ---------- */
  var menuOpener = null;
  var releaseMenuTrap = null;

  function openMenuDrawer(toggle) {
    var drawer = document.getElementById('MenuDrawer');
    if (!drawer) return;
    var overlay = document.querySelector('.menu-drawer__overlay');
    if (overlay) overlay.hidden = false;
    menuOpener = toggle || document.activeElement;
    document.body.classList.add('menu-drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    var close = drawer.querySelector('[data-menu-close]');
    if (close) close.focus();
    if (releaseMenuTrap) releaseMenuTrap();
    releaseMenuTrap = trapFocus(drawer);
  }
  function closeMenuDrawer() {
    var drawer = document.getElementById('MenuDrawer');
    if (!drawer || !document.body.classList.contains('menu-drawer-open')) return;
    document.body.classList.remove('menu-drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-menu-toggle]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    if (releaseMenuTrap) { releaseMenuTrap(); releaseMenuTrap = null; }
    if (menuOpener && document.contains(menuOpener)) menuOpener.focus();
    menuOpener = null;
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-menu-toggle]');
    if (toggle) {
      if (document.body.classList.contains('menu-drawer-open')) closeMenuDrawer();
      else openMenuDrawer(toggle);
      return;
    }
    if (e.target.closest('[data-menu-close]')) { closeMenuDrawer(); return; }
    var drawer = document.getElementById('MenuDrawer');
    if (drawer && document.body.classList.contains('menu-drawer-open') && drawer.contains(e.target) && e.target.closest('a')) {
      closeMenuDrawer();
    }
  });

  /* ---------- cart ---------- */
  var Cart = {
    sectionsToFetch: function () {
      var sections = ['cart-drawer', 'cart-icon-bubble'];
      if (document.body.classList.contains('template-cart')) sections.push('main-cart');
      return sections;
    },

    refreshFromSections: function (sections) {
      if (!sections) return;
      if (sections['main-cart']) {
        var mainCart = document.getElementById('MainCartContent');
        if (mainCart) {
          var dom3 = new DOMParser().parseFromString(sections['main-cart'], 'text/html');
          var fresh3 = dom3.getElementById('MainCartContent');
          if (fresh3) mainCart.innerHTML = fresh3.innerHTML;
        }
      }
      if (sections['cart-drawer']) {
        var drawer = document.getElementById('CartDrawer');
        if (drawer) {
          var dom = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          var fresh = dom.getElementById('CartDrawer');
          if (fresh) drawer.innerHTML = fresh.innerHTML;
        }
      }
      if (sections['cart-icon-bubble']) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          var dom2 = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          var fresh2 = dom2.getElementById('shopify-section-cart-icon-bubble') || dom2.body;
          bubble.innerHTML = fresh2.innerHTML;
        }
      }
    },

    add: function (body) {
      body.sections = Cart.sectionsToFetch();
      body.sections_url = window.location.pathname;
      return fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) throw new Error(data.description || window.cartStrings.error);
          Cart.refreshFromSections(data.sections);
          return data;
        });
    },

    change: function (line, quantity) {
      return fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: Cart.sectionsToFetch(), sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) throw new Error(data.description || window.cartStrings.error);
          Cart.refreshFromSections(data.sections);
          return data;
        });
    },

    openDrawer: function () {
      if (!document.body.classList.contains('cart-drawer-open')) cartOpener = document.activeElement;
      document.body.classList.add('cart-drawer-open');
      var drawer = document.getElementById('CartDrawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        var close = drawer.querySelector('.cart-drawer__close');
        if (close) close.focus();
        if (releaseCartTrap) releaseCartTrap();
        releaseCartTrap = trapFocus(drawer);
      }
    },
    closeDrawer: function () {
      document.body.classList.remove('cart-drawer-open');
      var drawer = document.getElementById('CartDrawer');
      if (drawer) drawer.setAttribute('aria-hidden', 'true');
      if (releaseCartTrap) { releaseCartTrap(); releaseCartTrap = null; }
      if (cartOpener && document.contains(cartOpener)) cartOpener.focus();
      cartOpener = null;
    }
  };
  var cartOpener = null;
  var releaseCartTrap = null;
  window.eraCart = Cart;

  /* After a successful ADD: open the drawer or go to the cart page, per theme setting */
  window.eraAfterAdd = function () {
    if (window.cartType === 'page') {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } else {
      Cart.openDrawer();
    }
  };

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      Cart.openDrawer();
    }
    if (e.target.closest('[data-cart-close]') || e.target.matches('.cart-drawer__overlay')) {
      Cart.closeDrawer();
    }
    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      Cart.change(Number(remove.dataset.line), 0).catch(function (err) { window.eraToast(err.message); });
    }
    var qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      var wrapper = qtyBtn.closest('.quantity-input');
      var input = wrapper.querySelector('input');
      var next = Number(input.value) + Number(qtyBtn.dataset.qtyChange);
      if (wrapper.dataset.line) {
        Cart.change(Number(wrapper.dataset.line), Math.max(0, next)).catch(function (err) { window.eraToast(err.message); });
      } else {
        input.value = Math.max(Number(input.min || 1), next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (document.body.classList.contains('cart-drawer-open')) Cart.closeDrawer();
    if (document.body.classList.contains('menu-drawer-open')) closeMenuDrawer();
  });

  /* Quick add buttons (single-variant products) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    Cart.add({ items: [{ id: Number(btn.dataset.quickAdd), quantity: 1 }] })
      .then(function () {
        window.eraToast(btn.dataset.quickAddMessage || window.variantStrings.addToCart, true);
        window.eraAfterAdd();
      })
      .catch(function (err) { window.eraToast(err.message); })
      .finally(function () { btn.disabled = false; });
  });

  /* ---------- count-up stats ---------- */
  function countUp(el) {
    var target = parseFloat(el.dataset.countUp);
    var decimals = Number(el.dataset.countDecimals || 0);
    var prefix = el.dataset.countPrefix || '';
    var suffix = el.dataset.countSuffix || '';
    var duration = 1300;
    var start = performance.now();
    function tick(now) {
      var p = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      var value = target * eased;
      el.textContent = prefix + (decimals ? value.toFixed(decimals) : Math.round(value).toLocaleString()) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }
  var statEls = document.querySelectorAll('[data-count-up]');
  if (statEls.length && 'IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var seen = new WeakSet();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !seen.has(entry.target)) {
          seen.add(entry.target);
          countUp(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    statEls.forEach(function (el) { io.observe(el); });
  } else {
    statEls.forEach(function (el) {
      var target = parseFloat(el.dataset.countUp) || 0;
      var decimals = Number(el.dataset.countDecimals || 0);
      el.textContent =
        (el.dataset.countPrefix || '') +
        (decimals ? target.toFixed(decimals) : Math.round(target).toLocaleString()) +
        (el.dataset.countSuffix || '');
    });
  }

  /* ---------- testimonial carousels ---------- */
  document.querySelectorAll('[data-carousel]').forEach(function (root) {
    var track = root.querySelector('.testimonials__track');
    var cards = Array.prototype.slice.call(root.querySelectorAll('.testimonial-card'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.carousel-dots button'));
    if (!track || cards.length === 0) return;
    var index = Math.min(1, cards.length - 1);
    var timer = null;
    var autoplay = root.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function render() {
      var viewport = root.querySelector('.testimonials__viewport') || root;
      var trackStyle = window.getComputedStyle(track);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 24;
      /* offsetWidth = layout width, unaffected by the scale() transform on inactive cards */
      var before = 0;
      for (var k = 0; k < index; k++) before += cards[k].offsetWidth + gap;
      var offset = viewport.clientWidth / 2 - cards[index].offsetWidth / 2 - before;
      track.style.transform = 'translateX(' + offset + 'px)';
      cards.forEach(function (c, i) { c.classList.toggle('is-active', i === index); });
      dots.forEach(function (d, i) { d.setAttribute('aria-current', i === index ? 'true' : 'false'); d.classList.toggle('is-active', i === index); });
    }
    function go(i) { index = (i + cards.length) % cards.length; render(); }
    function startTimer() {
      if (!autoplay) return;
      clearInterval(timer);
      timer = setInterval(function () { go(index + 1); }, Number(root.dataset.interval || 4000));
    }
    cards.forEach(function (c, i) { c.addEventListener('click', function () { go(i); startTimer(); }); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); startTimer(); }); });
    var touchX = null, touchY = null;
    track.addEventListener('touchstart', function (e) {
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      if (touchX === null) return;
      var dx = e.changedTouches[0].clientX - touchX;
      var dy = e.changedTouches[0].clientY - touchY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { go(index + (dx < 0 ? 1 : -1)); startTimer(); }
      touchX = touchY = null;
    }, { passive: true });
    window.addEventListener('resize', render);
    render();
    startTimer();
  });

  /* ---------- newsletter feedback ---------- */
  if (window.location.search.indexOf('customer_posted=true') > -1) {
    var msg = document.querySelector('[data-newsletter-success]');
    if (msg) msg.hidden = false;
  }

  /* ---------- recently viewed tracking ---------- */
  window.eraRecentlyViewed = {
    KEY: 'era:recently-viewed',
    get: function () {
      try { return JSON.parse(localStorage.getItem(this.KEY)) || []; } catch (e) { return []; }
    },
    track: function (handle) {
      var list = this.get().filter(function (h) { return h !== handle; });
      list.unshift(handle);
      try { localStorage.setItem(this.KEY, JSON.stringify(list.slice(0, 12))); } catch (e) { /* private mode */ }
    }
  };

  document.querySelectorAll('[data-recently-viewed]').forEach(function (root) {
    var current = root.dataset.currentHandle;
    var limit = Number(root.dataset.limit || 4);
    var handles = window.eraRecentlyViewed.get().filter(function (h) { return h !== current; }).slice(0, limit);
    if (!handles.length) return;
    var grid = root.querySelector('[data-recently-viewed-grid]');
    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '?section_id=' + root.dataset.sectionId)
          .then(function (r) { return r.ok ? r.text() : ''; })
          .catch(function () { return ''; });
      })
    ).then(function (chunks) {
      var html = chunks
        .map(function (chunk) {
          var doc = new DOMParser().parseFromString(chunk, 'text/html');
          var card = doc.querySelector('[data-rv-card]');
          return card ? card.outerHTML : '';
        })
        .join('');
      if (html) {
        grid.innerHTML = html;
        root.hidden = false;
      }
    });
  });
})();

/* ============================================================
   v2 — design-parity interactions
   ============================================================ */
(function () {
  'use strict';

  /* ---------- toast upsell (extends eraToast) ---------- */
  var baseToast = window.eraToast;
  window.eraToast = function (message, withUpsell) {
    var container = document.getElementById('ToastContainer');
    if (!container) return;
    var upsell = window.eraUpsell;
    if (!withUpsell || !upsell || !upsell.text) return baseToast(message);
    var toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML =
      '<div class="toast__row">' +
      '<span class="toast__check" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 11 11"><path d="M2 5.5l2.2 2.2L9 3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
      '<span class="toast__msg"></span></div>' +
      '<div class="toast__upsell"><span class="toast__upsell-text"></span><a class="toast__upsell-cta"></a></div>';
    toast.querySelector('.toast__msg').textContent = message;
    toast.querySelector('.toast__upsell-text').textContent = upsell.text;
    var cta = toast.querySelector('.toast__upsell-cta');
    cta.textContent = upsell.cta || 'Shop more';
    cta.href = upsell.url || '/collections/all';
    container.appendChild(toast);
    /* Duration comes from Theme settings -> Upsell toast -> Show for. */
    setTimeout(function () { toast.remove(); }, (window.eraUpsell && window.eraUpsell.duration) || 5200);
  };

  /* ---------- per-card image carousel ---------- */
  function cardLayers(media) { return media.querySelectorAll('.card-product__layer'); }
  function setCardIndex(media, i) {
    var layers = cardLayers(media);
    if (layers.length < 2) return;
    var n = layers.length;
    i = ((i % n) + n) % n;
    media.dataset.idx = i;
    layers.forEach(function (l, j) { l.classList.toggle('is-active', j === i); });
    media.querySelectorAll('.card-product__dots span').forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
  }

  document.addEventListener('click', function (e) {
    var prev = e.target.closest('[data-card-prev]');
    var next = e.target.closest('[data-card-next]');
    if (!prev && !next) return;
    e.preventDefault();
    e.stopPropagation();
    var media = (prev || next).closest('[data-card-media]');
    media.dataset.nav = '1';
    setCardIndex(media, Number(media.dataset.idx || 0) + (next ? 1 : -1));
  }, true);

  function mediaFromEvent(e) {
    var card = e.target.closest && e.target.closest('.card-product');
    return card ? card.querySelector('[data-card-media]') : null;
  }
  var touchOnly = window.matchMedia('(hover: none)');
  document.addEventListener('mouseover', function (e) {
    if (touchOnly.matches) return; /* touch screens: swipe handles image changes, no hover swap */
    var card = e.target.closest('.card-product');
    if (!card || card.contains(e.relatedTarget)) return;
    var media = card.querySelector('[data-card-media]');
    if (media && !media.dataset.nav && media.dataset.noHover === undefined) setCardIndex(media, 1);
  });
  document.addEventListener('mouseout', function (e) {
    if (touchOnly.matches) return;
    var card = e.target.closest('.card-product');
    if (!card || card.contains(e.relatedTarget)) return;
    var media = card.querySelector('[data-card-media]');
    if (media) { delete media.dataset.nav; setCardIndex(media, 0); }
  });

  var wheelT = 0;
  document.addEventListener('wheel', function (e) {
    var media = mediaFromEvent(e);
    if (!media) return;
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    var now = Date.now();
    if (now - wheelT < 500) return;
    wheelT = now;
    media.dataset.nav = '1';
    setCardIndex(media, Number(media.dataset.idx || 0) + (e.deltaX > 0 ? 1 : -1));
  }, { passive: true });

  /* ---------- quick view ---------- */
  function ensureQuickViewModal() {
    var dlg = document.getElementById('QuickViewModal');
    if (dlg) return dlg;
    dlg = document.createElement('dialog');
    dlg.className = 'modal modal--sheet';
    dlg.id = 'QuickViewModal';
    dlg.setAttribute('aria-label', 'Quick view');
    dlg.innerHTML = '<div class="modal__panel modal__panel--wide" style="padding:0;position:relative;">' +
      '<button class="modal__close" type="button" data-modal-close aria-label="Close" style="position:absolute;top:12px;right:12px;z-index:3;background:rgba(250,247,242,.95);border-radius:50%;width:32px;height:32px;box-shadow:0 4px 14px rgba(0,0,0,.18);display:flex;align-items:center;justify-content:center;">✕</button>' +
      '<div data-qv-body></div></div>';
    document.body.appendChild(dlg);
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg || e.target.closest('[data-modal-close]')) dlg.close();
    });
    dlg.addEventListener('close', function () { document.body.classList.remove('qv-open'); });
    return dlg;
  }

  function flyToModal(rect, src, dlg) {
    if (!rect || !src || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (dlg.querySelector('.era-fly-clone')) return; /* one flight at a time */
    var clone = document.createElement('img');
    clone.src = src;
    clone.className = 'era-fly-clone';
    clone.style.left = rect.left + 'px';
    clone.style.top = rect.top + 'px';
    clone.style.width = rect.width + 'px';
    clone.style.height = rect.height + 'px';
    /* Append INSIDE the dialog: dialogs render in the browser top layer, so a
       body-level clone would be painted underneath the backdrop and panel. */
    dlg.appendChild(clone);
    var start = performance.now();
    var prev = null, hidden = false;
    (function track() {
      var target = dlg.querySelector('[data-qv-main]');
      if (target) {
        if (!hidden) { target.style.opacity = '0'; hidden = true; }
        var t = target.getBoundingClientRect();
        /* Wait until the sheet/panel has finished animating: two consecutive
           frames with the target rect stable within 1.5px (as in the design). */
        if (t.width > 6 && prev &&
            Math.abs(prev.left - t.left) < 1.5 &&
            Math.abs(prev.top - t.top) < 1.5 &&
            Math.abs(prev.width - t.width) < 1.5) {
          var radius = getComputedStyle(target).borderRadius || '16px';
          requestAnimationFrame(function () {
            clone.style.left = t.left + 'px';
            clone.style.top = t.top + 'px';
            clone.style.width = t.width + 'px';
            clone.style.height = t.height + 'px';
            clone.style.borderRadius = radius;
          });
          setTimeout(function () {
            try { target.style.opacity = ''; clone.remove(); } catch (e) {}
          }, 500);
          return;
        }
        prev = t;
      }
      if (dlg.open && performance.now() - start < 1200) requestAnimationFrame(track);
      else {
        try { if (target) target.style.opacity = ''; clone.remove(); } catch (e) {}
      }
    })();
  }

  function wireQuickView(body) {
    var root = body.querySelector('[data-qv-root]');
    if (!root) return;
    var jsonEl = root.querySelector('[data-qv-json]');
    var data = null;
    try {
      data = JSON.parse(jsonEl ? jsonEl.textContent : '');
    } catch (err) {
      data = null;
    }
    if (!data || !Array.isArray(data.variants) || !data.variants.length) return;
    if (!Array.isArray(data.media)) data.media = [];
    var mainImg = root.querySelector('[data-qv-main]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-qv-thumb]'));
    var addBtn = root.querySelector('[data-qv-add]');
    if (!addBtn) return;
    var mediaIdx = 0;
    var dots = Array.prototype.slice.call(root.querySelectorAll('.qv-dots span'));

    function showMedia(i) {
      var n = data.media.length;
      if (!n || !mainImg) return;
      mediaIdx = ((i % n) + n) % n;
      mainImg.src = data.media[mediaIdx].src;
      thumbs.forEach(function (t, j) { t.classList.toggle('is-active', j === mediaIdx); });
      dots.forEach(function (d, j) { d.classList.toggle('is-active', j === mediaIdx); });
    }
    root.querySelectorAll('[data-qv-prev]').forEach(function (b) { b.addEventListener('click', function () { showMedia(mediaIdx - 1); }); });
    root.querySelectorAll('[data-qv-next]').forEach(function (b) { b.addEventListener('click', function () { showMedia(mediaIdx + 1); }); });
    thumbs.forEach(function (t) { t.addEventListener('click', function () { showMedia(Number(t.dataset.qvIdx)); }); });

    /* Touch swipe on the main image (40px horizontal-intent threshold) */
    var mainBox = root.querySelector('.quick-view__main');
    if (mainBox && data.media.length > 1) {
      var swipeX = null, swipeY = null;
      mainBox.addEventListener('touchstart', function (e) {
        swipeX = e.touches[0].clientX;
        swipeY = e.touches[0].clientY;
      }, { passive: true });
      mainBox.addEventListener('touchend', function (e) {
        if (swipeX === null) return;
        var dx = e.changedTouches[0].clientX - swipeX;
        var dy = e.changedTouches[0].clientY - swipeY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) showMedia(mediaIdx + (dx < 0 ? 1 : -1));
        swipeX = swipeY = null;
      }, { passive: true });
    }

    function selectedOptions() {
      return Array.prototype.map.call(root.querySelectorAll('[data-qv-option]'), function (set) {
        var checked = set.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    function currentVariant() {
      var opts = selectedOptions();
      if (!opts.length) return data.variants[0];
      return data.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
    }
    function refresh() {
      var v = currentVariant();
      var priceBox = root.querySelector('[data-qv-price]');
      root.querySelectorAll('[data-qv-option]').forEach(function (set) {
        var label = set.querySelector('[data-qv-selected]');
        var checked = set.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
      if (!v) {
        addBtn.disabled = true;
        addBtn.textContent = window.variantStrings.unavailable;
        return;
      }
      var html = '<div class="price price--large"><span class="price__current">' + window.eraMoney(v.price) + '</span>';
      if (v.compare && v.compare > v.price) {
        var off = Math.round((1 - v.price / v.compare) * 100);
        html += '<span class="price__compare">' + window.eraMoney(v.compare) + '</span><span class="price__badge-sale">' + off + '% off</span>';
      }
      if (priceBox) priceBox.innerHTML = html + '</div>';
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
      if (v.mediaId) {
        var mi = data.media.findIndex(function (m) { return m.id === v.mediaId; });
        if (mi >= 0) showMedia(mi);
      }
    }
    root.addEventListener('change', refresh);

    addBtn.addEventListener('click', function () {
      var v = currentVariant();
      if (!v || !v.available) return;
      if (addBtn.disabled) return;
      addBtn.disabled = true;
      window.eraCart.add({ items: [{ id: v.id, quantity: 1 }] })
        .then(function () {
          document.getElementById('QuickViewModal').close();
          window.eraToast((addBtn.dataset.title || 'Product') + ' added to cart', true);
          window.eraAfterAdd();
        })
        .catch(function (err) { window.eraToast(err.message); })
        .finally(function () { addBtn.disabled = false; });
    });
  }

  /* In-memory quick-view cache: handle → section HTML (serves repeat opens instantly) */
  var qvCache = new Map();

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    if (trigger.classList.contains('is-loading')) return;
    var handle = trigger.dataset.quickView;

    /* Capture the source card image before anything moves. */
    var rect = null, src = '';
    var media = trigger.closest('[data-card-media]');
    if (media) {
      var active = media.querySelector('.card-product__layer.is-active') || media.querySelector('img');
      if (active) { rect = active.getBoundingClientRect(); src = active.currentSrc || active.src; }
    }

    function openWith(text) {
      var doc = new DOMParser().parseFromString(text, 'text/html');
      var content = doc.querySelector('[data-qv-root]');
      if (!content) { window.location.href = '/products/' + handle; return; }
      var dlg = ensureQuickViewModal();
      /* Remove leftovers from any interrupted fly animation: a stale clone is
         appended to the dialog root (not the body), so innerHTML alone misses it. */
      dlg.querySelectorAll('.era-fly-clone').forEach(function (c) { c.remove(); });
      var body = dlg.querySelector('[data-qv-body]');
      body.innerHTML = '';
      body.appendChild(content);
      var freshMain = dlg.querySelector('[data-qv-main]');
      if (freshMain) freshMain.style.opacity = '';
      wireQuickView(body);
      /* Content is in place BEFORE the dialog opens: the sheet slides up once,
         already at full height — no half-open loading state. */
      dlg.showModal();
      document.body.classList.add('qv-open');
      flyToModal(rect, src, dlg);
    }

    if (qvCache.has(handle)) { openWith(qvCache.get(handle)); return; }

    trigger.setAttribute('aria-busy', 'true');
    trigger.classList.add('is-loading');
    fetch('/products/' + handle + '?section_id=quick-view')
      .then(function (r) {
        if (!r.ok) throw new Error('qv fetch failed');
        return r.text();
      })
      .then(function (text) {
        qvCache.set(handle, text);
        openWith(text);
      })
      .catch(function () { window.location.href = '/products/' + handle; })
      .finally(function () {
        trigger.removeAttribute('aria-busy');
        trigger.classList.remove('is-loading');
      });
  }, true);

  /* ---------- volume deal flip on scroll ---------- */
  var flipRoots = document.querySelectorAll('[data-flip-observe]');
  if (flipRoots.length && 'IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var fio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          setTimeout(function () { entry.target.classList.add('is-flipped'); }, 750);
          fio.unobserve(entry.target);
        }
      });
    }, { rootMargin: '-35% 0px -35% 0px', threshold: 0 });
    flipRoots.forEach(function (el) { fio.observe(el); });
  }
})();


/* ---------- hero slideshow ---------- */
(function () {
  document.querySelectorAll('[data-hero-carousel]').forEach(function (root) {
    var slides = root.querySelectorAll('.hero-split__slide');
    if (slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var i = 0;
    setInterval(function () {
      i = (i + 1) % slides.length;
      slides.forEach(function (sl, j) { sl.classList.toggle('is-active', j === i); });
    }, Number(root.dataset.interval || 4500));
  });
})();

/* ---------- checkout redirect toast ---------- */
document.addEventListener('click', function (e) {
  var btn = e.target.closest('[data-checkout-toast]');
  if (btn) window.eraToast(btn.dataset.checkoutToast || 'Redirecting to secure checkout…');
});


/* ---------- touch swipe on product card images ---------- */
(function () {
  var startX = null, startY = null, target = null;
  document.addEventListener('touchstart', function (e) {
    var card = e.target.closest && e.target.closest('.card-product');
    target = card ? card.querySelector('[data-card-media]') : null;
    if (!target) return;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    if (!target || startX === null) return;
    var dx = e.changedTouches[0].clientX - startX;
    var dy = e.changedTouches[0].clientY - startY;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      target.dataset.nav = '1';
      var layers = target.querySelectorAll('.card-product__layer');
      if (layers.length > 1) {
        var i = Number(target.dataset.idx || 0) + (dx < 0 ? 1 : -1);
        var n = layers.length;
        i = ((i % n) + n) % n;
        target.dataset.idx = i;
        layers.forEach(function (l, j) { l.classList.toggle('is-active', j === i); });
        target.querySelectorAll('.card-product__dots span').forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
      }
    }
    startX = startY = null; target = null;
  }, { passive: true });
})();


/* ============================================================
   v3 — Pass B: trust bar rotator, mobile filter drawer
   ============================================================ */
(function () {
  'use strict';

  /* ---------- trust bar rotator (mobile only) ---------- */
  document.querySelectorAll('[data-trust-rotate]').forEach(function (bar) {
    var items = Array.prototype.slice.call(bar.querySelectorAll('.trust-bar__item'));
    if (items.length < 2) return;
    var mobile = window.matchMedia('(max-width: 749px)');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var timer = null;
    var index = 0;
    function show(n) {
      items.forEach(function (el, j) { el.classList.toggle('is-active', j === n); });
    }
    function update() {
      if (timer) { clearInterval(timer); timer = null; }
      if (!mobile.matches || reduced.matches) { index = 0; show(0); return; }
      timer = setInterval(function () {
        index = (index + 1) % items.length;
        show(index);
      }, Number(bar.dataset.speed) || 3000);
    }
    if (mobile.addEventListener) {
      mobile.addEventListener('change', update);
      reduced.addEventListener('change', update);
    } else if (mobile.addListener) {
      mobile.addListener(update);
      reduced.addListener(update);
    }
    update();
  });

  /* ---------- mobile collection filter drawer ---------- */
  var filterOpener = null;
  function filterDrawer() { return document.getElementById('FilterDrawer'); }
  function openFilterDrawer(trigger) {
    var drawer = filterDrawer();
    if (!drawer) return;
    var overlay = document.querySelector('.filter-drawer__overlay');
    if (overlay) overlay.hidden = false;
    filterOpener = trigger || document.activeElement;
    requestAnimationFrame(function () { document.body.classList.add('filter-drawer-open'); });
    drawer.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var close = drawer.querySelector('.filter-drawer__close') || drawer.querySelector('[data-filter-close]');
    if (close) close.focus();
  }
  function closeFilterDrawer() {
    var drawer = filterDrawer();
    if (!drawer || !document.body.classList.contains('filter-drawer-open')) return;
    document.body.classList.remove('filter-drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-filter-open]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    var overlay = document.querySelector('.filter-drawer__overlay');
    if (overlay) setTimeout(function () { overlay.hidden = true; }, 260);
    if (filterOpener && document.contains(filterOpener)) filterOpener.focus();
    filterOpener = null;
  }
  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-filter-open]');
    if (open) { e.preventDefault(); openFilterDrawer(open); return; }
    if (e.target.closest('[data-filter-close]')) closeFilterDrawer();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.body.classList.contains('filter-drawer-open')) closeFilterDrawer();
  });
})();


/* ============================================================
   v4 — global size guide opener ([data-size-guide-open])
   Modes (theme setting, window.sizeGuideMode): sheet | page | scroll
   Works on the PDP and inside the injected quick-view modal.
   ============================================================ */
(function () {
  'use strict';

  var sgCache = new Map(); /* url → extracted HTML */

  function ensureDialog(popup) {
    var id = popup ? 'SizeGuidePopup' : 'SizeGuideSheet';
    var dlg = document.getElementById(id);
    if (dlg) return dlg;
    dlg = document.createElement('dialog');
    dlg.className = popup ? 'modal' : 'modal modal--sheet';
    dlg.id = id;
    dlg.setAttribute('aria-label', 'Size guide');
    dlg.innerHTML = '<div class="modal__panel modal__panel--wide" style="padding:0;position:relative;">' +
      '<button class="modal__close" type="button" data-modal-close aria-label="Close" style="position:absolute;top:12px;right:12px;z-index:3;background:rgba(250,247,242,.95);border-radius:50%;width:32px;height:32px;box-shadow:0 4px 14px rgba(0,0,0,.18);display:flex;align-items:center;justify-content:center;">✕</button>' +
      '<div data-sg-body></div></div>';
    document.body.appendChild(dlg);
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg || e.target.closest('[data-modal-close]')) dlg.close();
    });
    dlg.addEventListener('close', function () {
      dlg.querySelectorAll('.era-fly-clone').forEach(function (c) { c.remove(); });
      var mi = dlg.querySelector('[data-qv-main]');
      if (mi) mi.style.opacity = '';
    });
    return dlg;
  }

  function extractGuide(text) {
    var doc = new DOMParser().parseFromString(text, 'text/html');
    var node = doc.querySelector('.size-guide-layout') || doc.querySelector('.collapsible-pill .collapsible-pill__body');
    return node ? node.outerHTML : null;
  }

  function scrollMode() {
    document.querySelectorAll('dialog[open]').forEach(function (d) { d.close(); });
    var target = document.querySelector('.size-guide-layout') || document.querySelector('.size-table');
    if (!target) return;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  function openGuideHtml(html, popup) {
    var dlg = ensureDialog(popup);
    var body = dlg.querySelector('[data-sg-body]');
    body.innerHTML = html;
    if (!dlg.open) dlg.showModal();
  }

  function fetchMode(trigger, popup) {
    var url = (trigger.dataset.productUrl || window.location.pathname) + '?section_id=size-guide';
    if (sgCache.has(url)) { openGuideHtml(sgCache.get(url), popup); return; }
    trigger.setAttribute('aria-busy', 'true');
    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('sg fetch failed');
        return r.text();
      })
      .then(function (text) {
        var html = extractGuide(text);
        if (!html) throw new Error('sg extract failed');
        sgCache.set(url, html);
        openGuideHtml(html, popup);
      })
      .catch(function () { scrollMode(); })
      .finally(function () { trigger.removeAttribute('aria-busy'); });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-size-guide-open]');
    if (!trigger) return;
    e.preventDefault();
    var mode = window.sizeGuideMode || 'sheet';

    if (mode === 'page') {
      /* If a size-guide Page is selected on the product section, the theme renders a
         Liquid modal for it — prefer that. Otherwise show the size-guide TABLE in a
         centered popup modal (previously this fell through to the sheet, which made
         "Page popup modal" behave identically to "Quick-view style sheet"). */
      var pageModal = document.querySelector('dialog[id^="SizeGuideModal-"]');
      if (pageModal && typeof pageModal.showModal === 'function') {
        if (!pageModal.open) pageModal.showModal();
        return;
      }
      fetchMode(trigger, true);
      return;
    }

    if (mode === 'scroll') {
      /* scrollMode() closes every open <dialog>. If the trigger lives inside one
         (quick view, pack picker), scrolling would just tear the overlay away, so
         fall back to the sheet in that case. */
      if (trigger.closest('dialog[open]')) { fetchMode(trigger, false); return; }
      scrollMode();
      return;
    }

    fetchMode(trigger, false);
  });
})();
